import { router, useFocusEffect, useLocalSearchParams } from 'expo-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, ScrollView, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';
import Svg, { Circle } from 'react-native-svg';

import { BudgetEstimator } from '@/components/budget-estimator';
import { CATEGORY_ICONS, CategoryGrid } from '@/components/category-grid';
import { AppText } from '@/components/ui/app-text';
import { Card } from '@/components/ui/card';
import { IconTile } from '@/components/ui/icon-tile';
import { PrimaryButton } from '@/components/ui/primary-button';
import { TextField } from '@/components/ui/text-field';
import { getDb, TABLES } from '@/db';
import { useBudgetStore } from '@/store/budget-store';
import { fixedCostsTotalGrOf, useFixedCostsStore } from '@/store/fixed-costs-store';
import { useIncomeStore, monthTotalGrOf } from '@/store/income-store';
import { colors, radius, spacing } from '@/theme/tokens';
import type { BudgetEntry, CategoryId } from '@/types/domain';
import { formatPln, formatWhenPl } from '@/utils/format';

const MONTHS_PL = [
  'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
  'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień',
];

const DONUT_COLORS = ['#0093E5', '#7CC4F0', '#3A7A99', '#034E6F', '#BFC7D3'];

interface CategorySlice {
  categoryId: CategoryId;
  amountGr: number;
  pct: number;
  color: string;
}

/**
 * Budżet tab — month summary with category donut. Was a stack screen; promoted
 * to the tab slot freed by the "Dodaj" FAB (mock: podsumowanie_bud_etu_fab).
 */
export default function BudgetSummaryScreen() {
  const { t } = useTranslation();
  // subscribing to the store keeps the current month live after new entries
  const currentMonthEntries = useBudgetStore((s) => s.entries);
  const monthlyBudgetGr = useBudgetStore((s) => s.monthlyBudgetGr);
  const setMonthlyBudget = useBudgetStore((s) => s.setMonthlyBudget);
  const updateEntry = useBudgetStore((s) => s.updateEntry);
  const removeEntry = useBudgetStore((s) => s.removeEntry);
  const fixedCosts = useFixedCostsStore((s) => s.costs);
  const fixedTotalGr = fixedCostsTotalGrOf(fixedCosts);
  // Zarobki this month — to warn when the planned budget exceeds real income.
  const incomeEntries = useIncomeStore((s) => s.entries);

  // Per-entry edit/delete (owner: user mistyped an expense and couldn't fix it).
  const [editEntryId, setEditEntryId] = useState<string | null>(null);
  const [editEntryRaw, setEditEntryRaw] = useState('');
  const [editEntryCat, setEditEntryCat] = useState<CategoryId>('FOOD');
  const [editEntryNote, setEditEntryNote] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  // Tap a category to see WHAT it was spent on (owner/Maciek: "wejść w rozrywkę").
  const [expandedCat, setExpandedCat] = useState<CategoryId | null>(null);
  const openEntryEdit = (e: BudgetEntry) => {
    setEditEntryId(e.id);
    setEditEntryRaw(String(Math.round(e.amountGr / 100)));
    setEditEntryCat(e.categoryId);
    setEditEntryNote(e.note ?? '');
    setConfirmDeleteId(null);
  };
  const saveEntryEdit = async () => {
    if (!editEntryId) return;
    const digits = editEntryRaw.replace(/[^\d]/g, '');
    const gr = digits ? parseInt(digits, 10) * 100 : 0;
    if (gr > 0) await updateEntry(editEntryId, { amountGr: gr, categoryId: editEntryCat, note: editEntryNote });
    setEditEntryId(null);
  };

  const now = new Date();
  const [offset, setOffset] = useState(0); // 0 = current month, -1 = previous...
  const [allEntries, setAllEntries] = useState<BudgetEntry[] | null>(null);

  // Deep link from Start: "tap an expense there" opens it for editing here
  // (owner: had to go through "Zobacz wszystkie" first). Clear the param after
  // so switching tabs later doesn't re-open the editor.
  const { editEntry } = useLocalSearchParams<{ editEntry?: string }>();
  useEffect(() => {
    if (!editEntry) return;
    const e = currentMonthEntries.find((x) => x.id === editEntry);
    if (e) {
      setOffset(0);
      openEntryEdit(e);
    }
    router.setParams({ editEntry: undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editEntry]);

  // Always land on the current month when the tab is opened: a user who browsed
  // a past month, left, then added an expense was confused to see "0 zł wydane"
  // (the entry lands in the current month, not the one still on screen).
  useFocusEffect(
    useCallback(() => {
      setOffset(0);
    }, []),
  );

  // Budget editing — the only place monthlyBudgetGr can be set, so a fresh
  // (unseeded) install can start tracking pace at all.
  const [editingBudget, setEditingBudget] = useState(false);
  const [showCalc, setShowCalc] = useState(false);
  const [budgetRaw, setBudgetRaw] = useState('');
  const budgetInputGr = (() => {
    const digits = budgetRaw.replace(/[^\d]/g, '');
    return digits ? parseInt(digits, 10) * 100 : 0;
  })();
  const onSaveBudget = async () => {
    await setMonthlyBudget(budgetInputGr);
    setEditingBudget(false);
    setBudgetRaw('');
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const db = await getDb();
      const list = await db.list<BudgetEntry>(TABLES.entries);
      if (!cancelled) setAllEntries(list);
    })();
    return () => {
      cancelled = true;
    };
  }, [currentMonthEntries]);

  const shown = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const prefix = `${shown.getFullYear()}-${(shown.getMonth() + 1).toString().padStart(2, '0')}`;

  const monthEntries = useMemo(
    () => (offset === 0 ? currentMonthEntries : (allEntries ?? []).filter((e) => e.entryDate.startsWith(prefix))),
    [offset, currentMonthEntries, allEntries, prefix],
  );

  const totalGr = monthEntries.reduce((sum, e) => sum + e.amountGr, 0);

  // Over-budget warning (owner): a planned budget above real income should say so.
  const incomeThisMonthGr = monthTotalGrOf(incomeEntries, prefix);
  const budgetOverIncome = incomeThisMonthGr > 0 && monthlyBudgetGr > incomeThisMonthGr;
  // Editing the individual expenses only makes sense for the live (current) month
  // — that's the set the store keeps in memory and can upsert/remove.
  const canEditEntries = offset === 0;

  const slices: CategorySlice[] = useMemo(() => {
    const byCat = new Map<CategoryId, number>();
    for (const e of monthEntries) byCat.set(e.categoryId, (byCat.get(e.categoryId) ?? 0) + e.amountGr);
    const sorted = [...byCat.entries()].sort((a, b) => b[1] - a[1]);
    const top = sorted.slice(0, 4);
    const restGr = sorted.slice(4).reduce((sum, [, v]) => sum + v, 0);
    const merged: [CategoryId, number][] = restGr > 0 ? [...top, ['OTHER', restGr]] : top;
    return merged.map(([categoryId, amountGr], i) => ({
      categoryId,
      amountGr,
      pct: totalGr > 0 ? Math.round((100 * amountGr) / totalGr) : 0,
      color: DONUT_COLORS[i % DONUT_COLORS.length],
    }));
  }, [monthEntries, totalGr]);

  const biggest = slices[0];

  // Donut geometry
  const size = 220;
  const strokeWidth = 26;
  const r = (size - strokeWidth) / 2;
  const c = 2 * Math.PI * r;

  let acc = 0;
  const segments = slices.map((s) => {
    const frac = totalGr > 0 ? s.amountGr / totalGr : 0;
    const seg = { ...s, dash: `${Math.max(0, frac * c - 4)} ${c}`, offset: -acc * c };
    acc += frac;
    return seg;
  });

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top']}>
      <ScrollView contentContainerStyle={{ padding: spacing.screen, gap: spacing.md, paddingBottom: spacing.xl }}>
        <AppText variant="headlineLg" color={colors.navy}>
          {t('summary.title')}
        </AppText>

        {/* Monthly budget: value + edit (or a set-up prompt when still 0) */}
        <Card style={{ gap: spacing.sm }}>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <View style={{ flex: 1 }}>
              <AppText variant="labelMd" style={{ letterSpacing: 1.2, textTransform: 'uppercase' }}>
                {t('summary.budgetTitle')}
              </AppText>
              <AppText variant="headlineMd" color={colors.navy}>
                {formatPln(monthlyBudgetGr)}
              </AppText>
            </View>
            {!editingBudget && (
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={t('summary.budgetEdit')}
                onPress={() => setEditingBudget(true)}
                style={{ width: 44, height: 44, borderRadius: radius.full, backgroundColor: colors.primaryTile, alignItems: 'center', justifyContent: 'center' }}
              >
                <Ionicons name="pencil-outline" size={20} color={colors.navy} />
              </Pressable>
            )}
          </View>
          {monthlyBudgetGr === 0 && !editingBudget && (
            <>
              <AppText variant="bodyMd">{t('summary.budgetEmptyHint')}</AppText>
              <PrimaryButton label={t('summary.budgetSet')} onPress={() => setEditingBudget(true)} />
            </>
          )}
          {editingBudget && (
            <>
              <TextField
                label={t('summary.budgetTitle')}
                placeholder={t('summary.budgetPlaceholder')}
                keyboardType="numeric"
                value={budgetRaw}
                onChangeText={setBudgetRaw}
              />
              {/* "Tu zapisujemy wszystko" — owner: user didn't know whether to
                  enter one category or the whole month. */}
              <AppText variant="labelMd" color={colors.outline}>
                {t('summary.budgetWhatHint')}
              </AppText>
              {/* Guided calculator for users who can't estimate a total in their head. */}
              <Pressable
                accessibilityRole="button"
                onPress={() => setShowCalc((v) => !v)}
                style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs, minHeight: 40 }}
              >
                <Ionicons name={showCalc ? 'chevron-up' : 'calculator-outline'} size={18} color={colors.primary} />
                <AppText variant="labelLg" color={colors.primary}>
                  {t('summary.budgetHelpCta')}
                </AppText>
              </Pressable>
              {showCalc && (
                <BudgetEstimator
                  onApply={async (totalGr) => {
                    await setMonthlyBudget(totalGr);
                    setShowCalc(false);
                    setEditingBudget(false);
                    setBudgetRaw('');
                  }}
                />
              )}
              <PrimaryButton label={t('summary.budgetSave')} disabled={budgetInputGr <= 0} onPress={onSaveBudget} />
            </>
          )}
          {/* Warn when the plan exceeds this month's real income + link to Zarobki. */}
          {budgetOverIncome && !editingBudget && (
            <Pressable
              accessibilityRole="button"
              onPress={() => router.push('/income')}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: spacing.sm,
                backgroundColor: colors.warningBg,
                borderRadius: radius.card,
                padding: spacing.md,
              }}
            >
              <Ionicons name="warning-outline" size={20} color={colors.warning} />
              <AppText variant="bodyMd" style={{ flex: 1 }}>
                {t('summary.budgetOverIncome', { income: formatPln(incomeThisMonthGr) })}
              </AppText>
              <Ionicons name="chevron-forward" size={18} color={colors.warning} />
            </Pressable>
          )}
        </Card>

        {/* Month navigator */}
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
          <Pressable
            accessibilityRole="button"
            onPress={() => setOffset((o) => o - 1)}
            style={{ width: 44, height: 44, borderRadius: radius.full, backgroundColor: colors.primaryTile, alignItems: 'center', justifyContent: 'center' }}
          >
            <Ionicons name="chevron-back" size={20} color={colors.navy} />
          </Pressable>
          <View style={{ alignItems: 'center' }}>
            <AppText variant="headlineMd">{`${MONTHS_PL[shown.getMonth()]} ${shown.getFullYear()}`}</AppText>
            <AppText variant="bodyMd">{t('summary.subtitle')}</AppText>
          </View>
          <Pressable
            accessibilityRole="button"
            disabled={offset >= 0}
            onPress={() => setOffset((o) => Math.min(0, o + 1))}
            style={{ width: 44, height: 44, borderRadius: radius.full, backgroundColor: offset >= 0 ? colors.disabledBg : colors.primaryTile, alignItems: 'center', justifyContent: 'center' }}
          >
            <Ionicons name="chevron-forward" size={20} color={offset >= 0 ? colors.disabledText : colors.navy} />
          </Pressable>
        </View>

        {/* Donut */}
        <View style={{ alignItems: 'center', justifyContent: 'center' }}>
          <Svg width={size} height={size}>
            <Circle cx={size / 2} cy={size / 2} r={r} stroke={colors.divider} strokeWidth={strokeWidth} fill="none" />
            {segments.map((s) => (
              <Circle
                key={s.categoryId}
                cx={size / 2}
                cy={size / 2}
                r={r}
                stroke={s.color}
                strokeWidth={strokeWidth}
                fill="none"
                strokeLinecap="round"
                strokeDasharray={s.dash}
                strokeDashoffset={s.offset}
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
              />
            ))}
          </Svg>
          <View style={{ position: 'absolute', alignItems: 'center' }}>
            <AppText variant="headlineMd" color={colors.navy}>
              {formatPln(totalGr)}
            </AppText>
            <AppText variant="labelMd" style={{ letterSpacing: 1.2, textTransform: 'uppercase' }}>
              {t('summary.spent')}
            </AppText>
          </View>
        </View>

        {/* Insight: biggest category */}
        {biggest && totalGr > 0 && (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: spacing.sm,
              backgroundColor: '#FFF7E0',
              borderRadius: radius.card,
              padding: spacing.md,
            }}
          >
            <Ionicons name="bulb-outline" size={22} color={colors.warning} />
            <AppText variant="bodyLg" style={{ flex: 1 }}>
              {t('summary.insightBiggest', {
                category: t(`budget.categories.${biggest.categoryId}`),
                pct: biggest.pct,
              })}
            </AppText>
          </View>
        )}

        {/* Categories */}
        <Card style={{ gap: 0, padding: 0 }}>
          <View style={{ padding: spacing.md, paddingBottom: spacing.xs }}>
            <AppText variant="labelMd" style={{ letterSpacing: 1.2, textTransform: 'uppercase' }}>
              {t('summary.categories')}
            </AppText>
          </View>
          {slices.length === 0 && (
            <View style={{ padding: spacing.md }}>
              <AppText variant="bodyMd">{t('summary.empty')}</AppText>
            </View>
          )}
          {slices.map((s, idx) => {
            const open = expandedCat === s.categoryId;
            // Named line items for this category (owner/Maciek: "wejść w rozrywkę
            // i zobaczyć Allegro 500, kino 40") — newest first, as stored.
            const items = monthEntries.filter((e) => e.categoryId === s.categoryId);
            return (
              <View key={s.categoryId} style={{ borderTopWidth: idx === 0 ? 0 : 1, borderTopColor: colors.divider }}>
                <Pressable
                  accessibilityRole="button"
                  onPress={() => setExpandedCat(open ? null : s.categoryId)}
                  style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, padding: spacing.md }}
                >
                  <View style={{ width: 12, height: 12, borderRadius: radius.full, backgroundColor: s.color }} />
                  <View style={{ flex: 1 }}>
                    <AppText variant="bodyLg">{t(`budget.categories.${s.categoryId}`)}</AppText>
                    <AppText variant="labelMd">{t('summary.pctOfBudget', { pct: s.pct })}</AppText>
                  </View>
                  <AppText variant="labelLg">{formatPln(s.amountGr)}</AppText>
                  <Ionicons name={open ? 'chevron-down' : 'chevron-forward'} size={16} color={colors.outlineVariant} />
                </Pressable>
                {open &&
                  items.map((e) => (
                    <View
                      key={e.id}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: spacing.sm,
                        paddingVertical: spacing.xs,
                        paddingLeft: spacing.md + 20,
                        paddingRight: spacing.md,
                      }}
                    >
                      <AppText variant="bodyMd" color={colors.textSecondary} style={{ flex: 1 }}>
                        {e.note ?? t('summary.noteEmpty')}
                      </AppText>
                      <AppText variant="bodyMd">{formatPln(e.amountGr)}</AppText>
                    </View>
                  ))}
              </View>
            );
          })}
        </Card>

        {/* Individual operations — tap to edit the amount/category or delete a
            mistyped one (owner: "kliknął w wydatek i nie da się zmienić/usunąć"). */}
        {monthEntries.length > 0 && (
          <Card style={{ padding: 0 }}>
            <View style={{ padding: spacing.md, paddingBottom: spacing.xs }}>
              <AppText variant="labelMd" style={{ letterSpacing: 1.2, textTransform: 'uppercase' }}>
                {t('summary.operations')}
              </AppText>
            </View>
            {monthEntries.map((e, idx) => {
              const editing = editEntryId === e.id;
              return (
                <View key={e.id} style={{ borderTopWidth: idx === 0 ? 0 : 1, borderTopColor: colors.divider }}>
                  <Pressable
                    accessibilityRole="button"
                    disabled={!canEditEntries}
                    onPress={() => (editing ? setEditEntryId(null) : openEntryEdit(e))}
                    style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, padding: spacing.md, minHeight: 56 }}
                  >
                    <IconTile icon={CATEGORY_ICONS[e.categoryId]} bg={colors.primaryTile} fg={colors.navy} size={40} />
                    <View style={{ flex: 1 }}>
                      <AppText variant="labelLg">{e.note ?? t(`budget.categories.${e.categoryId}`)}</AppText>
                      <AppText variant="bodyMd">
                        {e.note ? `${t(`budget.categories.${e.categoryId}`)} · ${formatWhenPl(e.createdAt)}` : formatWhenPl(e.createdAt)}
                      </AppText>
                    </View>
                    <AppText variant="labelLg" color={colors.error}>
                      −{formatPln(e.amountGr)}
                    </AppText>
                    {canEditEntries && (
                      <Ionicons name={editing ? 'chevron-down' : 'chevron-forward'} size={18} color={colors.outlineVariant} />
                    )}
                  </Pressable>
                  {editing && (
                    <View style={{ padding: spacing.md, paddingTop: 0, gap: spacing.sm }}>
                      <TextField
                        label={t('summary.editAmountLabel')}
                        keyboardType="numeric"
                        value={editEntryRaw}
                        onChangeText={setEditEntryRaw}
                      />
                      <TextField
                        label={t('budget.noteLabel')}
                        placeholder={t('budget.notePlaceholder')}
                        value={editEntryNote}
                        onChangeText={setEditEntryNote}
                      />
                      <CategoryGrid selected={editEntryCat} onSelect={setEditEntryCat} />
                      <PrimaryButton label={t('common.save')} onPress={saveEntryEdit} />
                      <Pressable
                        accessibilityRole="button"
                        onPress={() => {
                          if (confirmDeleteId === e.id) {
                            void removeEntry(e.id);
                            setEditEntryId(null);
                            setConfirmDeleteId(null);
                          } else {
                            setConfirmDeleteId(e.id);
                          }
                        }}
                        style={{ minHeight: 40, alignItems: 'center', justifyContent: 'center' }}
                      >
                        <AppText variant="labelMd" color={colors.error}>
                          {confirmDeleteId === e.id ? t('summary.deleteConfirm') : t('summary.deleteEntry')}
                        </AppText>
                      </Pressable>
                    </View>
                  )}
                </View>
              );
            })}
          </Card>
        )}

        {/* Fixed costs entry point (the owner's "Wspólne mieszkanko" sheet) */}
        <Pressable accessibilityRole="button" onPress={() => router.push('/fixed-costs')}>
          <Card style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
            <Ionicons name="receipt-outline" size={24} color={colors.navy} />
            <View style={{ flex: 1, gap: 2 }}>
              <AppText variant="labelLg">{t('fixedCosts.title')}</AppText>
              <AppText variant="bodyMd">
                {fixedTotalGr > 0
                  ? t('fixedCosts.budgetRow', { total: formatPln(fixedTotalGr) })
                  : t('fixedCosts.budgetRowEmpty')}
              </AppText>
            </View>
            <Ionicons name="chevron-forward" size={20} color={colors.outlineVariant} />
          </Card>
        </Pressable>
      </ScrollView>
    </SafeAreaView>
  );
}
